import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface MobileChromeContextValue {
	tabBarHidden: boolean;
	hideTabBar: () => () => void;
}

const MobileChromeContext = createContext<MobileChromeContextValue>({
	tabBarHidden: false,
	hideTabBar: () => () => {},
});

/** Tracks which mounted screens want the bottom tab bar out of the way. */
export function MobileChromeProvider({ children }: { children: ReactNode }) {
	const [hiders, setHiders] = useState(0);

	const hideTabBar = useCallback(() => {
		setHiders((n) => n + 1);
		return () => setHiders((n) => n - 1);
	}, []);

	const value = useMemo(() => ({ tabBarHidden: hiders > 0, hideTabBar }), [hiders, hideTabBar]);

	// Lets CSS drop --mobile-tabbar-height to the home-indicator inset while
	// a pushed screen hides the bar, so fixed UI moves down with it.
	useEffect(() => {
		document.documentElement.toggleAttribute('data-tabbar-hidden', hiders > 0);
		return () => document.documentElement.removeAttribute('data-tabbar-hidden');
	}, [hiders]);

	return <MobileChromeContext.Provider value={value}>{children}</MobileChromeContext.Provider>;
}

export const useMobileChrome = () => useContext(MobileChromeContext);

/**
 * Hides the bottom tab bar while the calling screen is mounted, like a native
 * app does for pushed detail screens (a conversation, an editor).
 */
export function useHideMobileTabBar(active = true) {
	const { hideTabBar } = useMobileChrome();
	useEffect(() => {
		if (!active) return;
		return hideTabBar();
	}, [active, hideTabBar]);
}

/** Component form of useHideMobileTabBar for class components and JSX branches. */
export function HideMobileTabBar({ active = true }: { active?: boolean }) {
	useHideMobileTabBar(active);
	return null;
}
